import { ActionTree } from 'vuex'
import { RootState } from '@/store'
import { SmartMarketingState } from '@/store/smartMarketing/index'
import { CredentialsType, EmmaDefaults } from '@/api/types'
import { CompanyAPI, RealworksKenmerkAPI } from '@/api/backend-client'
import { Company } from '@/api/model/company'
import { User } from '@/api/model/user'
import { ActiveCampaignAPI } from '@/api/client/active-campaign'
import { ActiveCampaignCredentials } from '@/api/model/credentials'
import { SmartMarketingAPI } from '@/api/client/smart-marketing'
import { RealworksAPI } from '@/api/client/realworks'

export const actions: ActionTree<SmartMarketingState, RootState> = {
    async load({ commit, dispatch, getters, state }): Promise<void> {
        try {
            commit('load')

            const company = await CompanyAPI.reloadCurrentCompany()
            const credentials = company.credentials
                .filter(credential => getters.supportedPlatforms
                    .includes(credential.credentialsType))

            if (credentials.length > 0) {
                commit('loadSuccess', credentials[0])
            }

            // if activated then fetch additional data
            if (getters.isActivated) {
                await dispatch('validate')
                await dispatch('updateRealworksKenmerken')
            }

        } catch (error) {
            commit('loadFailure', error)
        } finally {
            commit('updateRealworksAgendaEnabled', state.syncedRealworksAgendatypes.length > 0)
            commit('updateRealworksTaakEnabled', state.syncedRealworksTaaktypes.length > 0)
        }
    },

    async save({ commit, dispatch, state }): Promise<void> {
        try {
            commit('updateRealworksAgendaEnabled', state.syncedRealworksAgendatypes.length > 0)
            commit('updateRealworksTaakEnabled', state.syncedRealworksTaaktypes.length > 0)
            commit('save')
            await CompanyAPI.updateActiveCampaignCredentials(CompanyAPI.currentCompany?.id, ActiveCampaignCredentials.fromData({
                includeInternalContacts: state.includeInternalContacts,
                includeExternalContacts: state.includeExternalContacts,
                includeRealworksRelaties: state.includeRealworksRelaties,
                syncedRealworksKenmerken: state.syncedRealworksKenmerken,
                excludedRealworksKenmerken: state.excludedRealworksKenmerken,
                syncedRealworksRelatietypes: state.syncedRealworksRelatietypes,
                syncedRealworksStatus: state.syncedRealworksStatus,
                updatedFrom: state.updatedFrom,
                updatedUntil: state.updatedUntil,
                apiUrl: state.apiUrl,
                apiKey: state.apiKey,
                eventTrackingAccountId: state.eventTrackingAccountId,
                eventTrackingKey: state.eventTrackingKey,
                defaultTeams: state.defaultTeams,
                defaultStatus: state.defaultStatus,
                emmaDefaults: state.emmaDefaults,
                syncedRealworksAgendatypes: state.syncedRealworksAgendatypes,
                syncedRealworksTaaktypes: state.syncedRealworksTaaktypes,
                enabled: state.enabled,
                user: state.updatedBy instanceof User ? state.updatedBy.resourceIdentifier : null,
            }))
            commit('saveSuccess')
            dispatch('load')
        } catch (error) {
            commit('saveFailure', error)
        }
    },

    async validate({ commit, state }): Promise<void> {
        try {
            commit('validate')
            commit('validateSuccess', await ActiveCampaignAPI
                .validateCredentials(ActiveCampaignCredentials.fromData({
                    apiUrl: state.apiUrl,
                    apiKey: state.apiKey,
                })),
            )

        } catch (error) {
            commit('validateFailure', error)
        }
    },

    async activate({ commit, dispatch }): Promise<void> {
        try {

            // ensure that credentials are saved
            await dispatch('save')

            commit('activate')
            await ActiveCampaignAPI.activate(ActiveCampaignCredentials.fromData({ company: CompanyAPI.currentCompany }))
            commit('activateSuccess')

        } catch (error) {
            commit('activateFailure', error)
        }
    },

    async updateContactCount({ commit, state }): Promise<void> {
        try {
            commit('updateContactCount')
            const count = await SmartMarketingAPI.getContactsCount(ActiveCampaignCredentials.fromData({
                company: CompanyAPI.currentCompany,
                includeInternalContacts: state.includeInternalContacts,
                includeExternalContacts: state.includeExternalContacts,
                includeRealworksRelaties: state.includeRealworksRelaties,
                syncedRealworksKenmerken: state.syncedRealworksKenmerken,
                excludedRealworksKenmerken: state.excludedRealworksKenmerken,
                syncedRealworksRelatietypes: state.syncedRealworksRelatietypes,
                syncedRealworksStatus: state.syncedRealworksStatus,
                updatedFrom: state.updatedFrom,
                updatedUntil: state.updatedUntil,
            }))
            commit('updateContactCountSuccess', count)

        } catch (error) {
            commit('updateContactCountFailure', error)
        }
    },

    async updateRealworksKenmerken({ commit }): Promise<void> {
        try {
            commit('updateRealworksKenmerken')
            commit('updateRealworksKenmerkenSuccess', await RealworksKenmerkAPI.getAll())

        } catch (error) {
            commit('updateRealworksKenmerkenFailure', error)
        }
    },

    async updateRealworksRelatietypes({ commit }): Promise<void> {
        try {
            commit('updateRealworksRelatietypes')
            commit('updateRealworksRelatietypesSuccess', await RealworksAPI.getRelatietypes())

        } catch (error) {
            commit('updateRealworksRelatietypesFailure', error)
        }
    },

    async updateRealworksAgendatypes({ commit }): Promise<void> {
        try {
            commit('updateRealworksAgendatypes')
            commit('updateRealworksAgendatypesSuccess', await RealworksAPI.getAgendatypes())

        } catch (error) {
            commit('updateRealworksAgendatypesFailure', error)
        }
    },

    async updateRealworksTaaktypes({ commit }): Promise<void> {
        try {
            commit('updateRealworksTaaktypes')
            commit('updateRealworksTaaktypesSuccess', await RealworksAPI.getTaaktypes())

        } catch (error) {
            commit('updateRealworksTaaktypesFailure', error)
        }
    },

    async updateRealworksAgendaEnabled({ commit }, enabled: boolean): Promise<void> {
        if (!enabled) {
            commit('updateSyncedRealworksAgendatypes', [])
        }

        commit('updateRealworksAgendaEnabled', enabled)
    },

    async updateRealworksTaakEnabled({ commit }, enabled: boolean): Promise<void> {
        if (!enabled) {
            commit('updateSyncedRealworksTaaktypes', [])
        }

        commit('updateRealworksTaakEnabled', enabled)
    },

    /* Credentials */

    async updateCompany({ commit }, company: Company): Promise<void> {
        commit('updateCompany', company)
    },

    async updateId({ commit }, id: string): Promise<void> {
        commit('updateId', id)
    },

    async updateEnabled({ commit }, enabled: boolean): Promise<void> {
        commit('updateEnabled', enabled)
    },

    async updateCreatedAt({ commit }, createdAt: Date): Promise<void> {
        commit('updateCreatedAt', createdAt)
    },

    async updateCreatedBy({ commit }, createdBy: User): Promise<void> {
        commit('updateCreatedBy', createdBy)
    },

    async updateUpdatedAt({ commit }, updatedAt: Date): Promise<void> {
        commit('updateUpdatedAt', updatedAt)
    },

    async updateUpdatedBy({ commit }, updatedBy: string): Promise<void> {
        commit('updateUpdatedBy', updatedBy)
    },

    async updateCredentialsType({ commit }, credentialsType: CredentialsType): Promise<void> {
        commit('updateCredentialsType', credentialsType)
    },

    /* Shared */

    async updateIncludeInternalContacts({ commit, dispatch }, includeInternalContacts: boolean): Promise<void> {
        commit('updateIncludeInternalContacts', includeInternalContacts)
        await dispatch('updateContactCount')
    },

    async updateIncludeExternalContacts({ commit, dispatch }, includeExternalContacts: boolean): Promise<void> {
        commit('updateIncludeExternalContacts', includeExternalContacts)
        await dispatch('updateContactCount')
    },

    async updateIncludeRealworksRelaties({ commit, dispatch }, includeRealworksRelaties: boolean): Promise<void> {
        commit('updateIncludeRealworksRelaties', includeRealworksRelaties)
        await dispatch('updateContactCount')
    },

    async updateSyncedRealworksKenmerken({ commit, dispatch }, syncedRealworksKenmerken: string[]): Promise<void> {
        commit('updateSyncedRealworksKenmerken', syncedRealworksKenmerken)
        await dispatch('updateContactCount')
    },

    async updateExcludedRealworksKenmerken({ commit, dispatch }, excludedRealworksKenmerken: string[]): Promise<void> {
        commit('updateExcludedRealworksKenmerken', excludedRealworksKenmerken)
        await dispatch('updateContactCount')
    },

    async updateSyncedRealworksRelatietypes({ commit, dispatch }, syncedRealworksRelatietypes: string[]): Promise<void> {
        commit('updateSyncedRealworksRelatietypes', syncedRealworksRelatietypes)
        await dispatch('updateContactCount')
    },

    async updateSyncedRealworksStatus({ commit, dispatch }, syncedRealworksStatus: boolean|null): Promise<void> {
        commit('updateSyncedRealworksStatus', syncedRealworksStatus)
        await dispatch('updateContactCount')
    },

    async updateUpdatedFrom({ commit, dispatch }, updatedFrom: Date): Promise<void> {
        commit('updateUpdatedFrom', updatedFrom)
        await dispatch('updateContactCount')
    },

    async updateUpdatedUntil({ commit, dispatch }, updatedUntil: Date): Promise<void> {
        commit('updateUpdatedUntil', updatedUntil)
        await dispatch('updateContactCount')
    },

    async updateApiKey({ commit }, apiKey: string): Promise<void> {
        commit('updateApiKey', apiKey)
    },

    async updateListId({ commit }, listId: string): Promise<void> {
        commit('updateListId', listId)
    },

    /* ActiveCampaignCredentials */

    async updateApiUrl({ commit }, apiUrl: string): Promise<void> {
        commit('updateApiUrl', apiUrl)
    },

    async updateEventTrackingEnabled({ commit }, eventTrackingEnabled: boolean): Promise<void> {
        commit('updateEventTrackingEnabled', eventTrackingEnabled)
    },

    async updateEventTrackingAccountId({ commit }, eventTrackingAccountId: string): Promise<void> {
        commit('updateEventTrackingAccountId', eventTrackingAccountId)
    },

    async updateEventTrackingKey({ commit }, eventTrackingKey: string): Promise<void> {
        commit('updateEventTrackingKey', eventTrackingKey)
    },

    async updateDefaultTeams({ commit }, defaultTeams: string[]): Promise<void> {
        commit('updateDefaultTeams', defaultTeams)
    },

    async updateDefaultStatus({ commit }, defaultStatus: string): Promise<void> {
        commit('updateDefaultStatus', defaultStatus)
    },

    async updateSyncedRealworksAgendatypes({ commit }, syncedRealworksAgendatypes: string[]): Promise<void> {
        commit('updateSyncedRealworksAgendatypes', syncedRealworksAgendatypes)
    },

    async updateSyncedRealworksTaaktypes({ commit }, syncedRealworksTaaktypes: string[]): Promise<void> {
        commit('updateSyncedRealworksTaaktypes', syncedRealworksTaaktypes)
    },

    async updateAgendaListId({ commit }, agendaListId: string|null): Promise<void> {
        commit('updateAgendaListId', agendaListId)
    },

    async updateTaakListId({ commit }, taakListId: string|null): Promise<void> {
        commit('updateTaakListId', taakListId)
    },

    async updateViewingListId({ commit }, viewingListId: string|null): Promise<void> {
        commit('updateViewingListId', viewingListId)
    },

    async updateEmmaListId({ commit }, emmaListId: string|null): Promise<void> {
        commit('updateEmmaListId', emmaListId)
    },

    async updateEmmaDefaults({ commit }, emmaDefaults: EmmaDefaults): Promise<void> {
        commit('updateEmmaDefaults', emmaDefaults)
    },

    /* MailchimpCredentials */

    async updateServer({ commit }, server: string): Promise<void> {
        commit('updateServer', server)
    },
}
